/**
 * Indicações de thread (resposta / encaminhamento) a partir do payload bruto dos canais.
 * Cada provedor expõe campos diferentes; aqui só sinalizamos o que vier sem inventar conteúdo citado.
 */

import { parseSharedContactFromMessage } from "./parseSharedContact";

function parseJsonMaybe(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Envio fora do Xpro (app WhatsApp, Instagram, Messenger); usa senderType e/ou json_whatsapp. */
export function isWhatsappAppOutboundFromMessage(message) {
  const st = (message?.senderType || "").toString().toLowerCase();
  if (st === "whatsapp_app" || st === "meta_page") return true;
  const j = parseJsonMaybe(message?.json_whatsapp);
  if (!j || typeof j !== "object") return false;
  return (
    (j.xpro_origin === "whatsapp_app" || j.xpro_origin === "meta_page") &&
    j.xpro_direction === "outbound_external"
  );
}

export function getExternalOutboundSenderLabel(message) {
  const st = (message?.senderType || "").toString().toLowerCase();
  const j = parseJsonMaybe(message?.json_whatsapp);
  const origin = j?.xpro_origin;
  const originTipo = Number(message?.originTipo ?? message?.messageChannelTipo);
  if (st === "meta_page" || origin === "meta_page") {
    return originTipo === 21 ? "Enviado pelo app do Instagram" : "Enviado pelo Messenger";
  }
  return "Enviado pelo app do WhatsApp";
}

export function getExternalOutboundChipLabel(message) {
  const st = (message?.senderType || "").toString().toLowerCase();
  const j = parseJsonMaybe(message?.json_whatsapp);
  const origin = j?.xpro_origin;
  const originTipo = Number(message?.originTipo ?? message?.messageChannelTipo);
  if (st === "meta_page" || origin === "meta_page") {
    return originTipo === 21 ? "Instagram (app)" : "Messenger (app)";
  }
  return "WhatsApp (app)";
}

function metaWhatsappMessageFromWebhook(parsed) {
  const entry = parsed?.entry;
  if (!Array.isArray(entry)) return null;
  for (const e of entry) {
    const changes = e?.changes;
    if (!Array.isArray(changes)) continue;
    for (const ch of changes) {
      const msgs = ch?.value?.messages;
      if (Array.isArray(msgs) && msgs[0]) return msgs[0];
    }
  }
  return null;
}

function metaMessagingFromWebhook(parsed) {
  const entry = parsed?.entry;
  const e0 = Array.isArray(entry) ? entry[0] : null;
  const arr = e0?.messaging;
  return Array.isArray(arr) ? arr[0] : null;
}

function gupshupInnerPayload(parsed) {
  if (parsed?.type !== "message") return null;
  const p = parsed?.payload;
  return p && typeof p === "object" ? p : null;
}

function pickTruthyString(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return s || "";
}

/** Normaliza Message-ID de e-mail para comparação (remove <>, trim, lower). */
export function normalizeEmailMessageId(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();
  const m = s.match(/<([^>]+)>/);
  if (m) s = m[1];
  return s.trim().toLowerCase();
}

function stripHtmlToPlain(s) {
  if (s == null || typeof s !== "string") return "";
  return s
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * IDs referenciados como “mensagem respondida” no payload (ordem de tentativa de match).
 * @param {object} message
 * @returns {string[]}
 */
export function extractReplyReferenceIds(message) {
  const ids = [];
  const parsed = parseJsonMaybe(message?.json_whatsapp);

  const legQ = pickTruthyString(message?.quotedMessageIdWhatsapp);
  if (legQ) ids.push(legQ);

  const xt0 = parsed?.xpro_thread;
  if (xt0 && typeof xt0 === "object") {
    const snap = pickTruthyString(xt0.replyToWaMessageId);
    if (snap) ids.push(snap);
    const loc = xt0.referencedLocalId != null ? pickTruthyString(xt0.referencedLocalId) : "";
    if (loc) ids.push(loc);
  }

  const waMsg = metaWhatsappMessageFromWebhook(parsed);
  if (waMsg && typeof waMsg === "object") {
    const ctx = waMsg.context && typeof waMsg.context === "object" ? waMsg.context : {};
    const id = pickTruthyString(ctx.id);
    if (id) ids.push(id);
  }

  const gsPayload = gupshupInnerPayload(parsed);
  const inner = gsPayload?.payload && typeof gsPayload.payload === "object" ? gsPayload.payload : null;
  const gsCtx = inner?.context && typeof inner.context === "object" ? inner.context : null;
  if (gsCtx) {
    const r = pickTruthyString(gsCtx.gsId || gsCtx.id);
    if (r) ids.push(r);
  }

  const m = metaMessagingFromWebhook(parsed);
  const fbMsg = m?.message && typeof m.message === "object" ? m.message : null;
  if (fbMsg?.reply_to && pickTruthyString(fbMsg.reply_to.mid)) {
    ids.push(pickTruthyString(fbMsg.reply_to.mid));
  }

  if (
    parsed &&
    typeof parsed === "object" &&
    !parsed.entry &&
    parsed.type !== "message" &&
    !parsed.messaging
  ) {
    const r = pickTruthyString(
      parsed.quoted_message_id ||
        parsed.id_mensagem_respondida ||
        parsed.mensagem_citada_id ||
        parsed.citacao_id ||
        (parsed.mensagem_citada && typeof parsed.mensagem_citada === "object"
          ? parsed.mensagem_citada.id || parsed.mensagem_citada.mensagem_id
          : "")
    );
    if (r) ids.push(r);
  }

  const tags = message?.emailOriginalTags;
  if (tags && typeof tags === "object") {
    const irt = pickTruthyString(tags["In-Reply-To"] || tags["in-reply-to"]);
    if (irt) {
      for (const part of irt.split(/\s+/)) {
        const n = normalizeEmailMessageId(part);
        if (n) ids.push(n);
      }
    }
  }

  const seen = new Set();
  const out = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function quotedAuthorLabel(msg, user) {
  const typ = (msg?.type || "").toString().toLowerCase();
  if (typ === "auto_response" || typ === "automaticanswer") {
    const n = msg?.senderName && String(msg.senderName).trim();
    return n || "Assistente virtual";
  }
  const uraU = msg?.uraUser != null ? Number(msg.uraUser) : 0;
  if (uraU === 1) {
    const n = msg?.senderName && String(msg.senderName).trim();
    return n || "Chatbot";
  }

  const senderType = (msg?.senderType || "").toLowerCase();
  const sender = (msg?.sender || "").toLowerCase();
  const isWaAppOutbound = isWhatsappAppOutboundFromMessage(msg);
  const isClient =
    !isWaAppOutbound &&
    (sender === "cliente" ||
    sender === "client" ||
    senderType === "client" ||
    senderType === "customer" ||
    msg?.channel === "customer");
  const isAgentOrOperator =
    sender === "agent" ||
    sender === "operator" ||
    senderType === "agent" ||
    senderType === "operator" ||
    senderType === "ai_agent" ||
    senderType === "chatbot";
  const loggedUserId = user?.id != null ? String(user.id) : null;
  const msgUserId =
    msg?.senderId != null
      ? String(msg.senderId)
      : msg?.id_usuario != null
      ? String(msg.id_usuario)
      : null;
  const isLoggedInUser = !!loggedUserId && !!msgUserId && msgUserId === loggedUserId;
  const isOtherUser = !!user && !isClient && isAgentOrOperator && !isLoggedInUser;

  const name = msg?.senderName && String(msg.senderName).trim();
  if (name) return name;
  if (isClient) return "Cliente";
  if (isLoggedInUser) return "Você";
  if (isOtherUser) return "Atendente";
  if (isAgentOrOperator) return "Atendente";
  if (senderType === "whatsapp_app" || senderType === "meta_page") {
    return getExternalOutboundSenderLabel(msg);
  }
  return "Usuário";
}

export function quotedSnippetFromMessage(msg) {
  const shared = parseSharedContactFromMessage(msg);
  if (shared?.displayName) {
    const label = `Contato: ${shared.displayName}`;
    return label.length > 160 ? `${label.slice(0, 157)}…` : label;
  }
  if ((msg?.type || "").toString().toLowerCase() === "contact") {
    return "[Contato]";
  }
  const t = msg?.text;
  if (typeof t === "string" && t.trim()) {
    const plain = stripHtmlToPlain(t);
    if (plain) return plain.length > 160 ? `${plain.slice(0, 157)}…` : plain;
  }
  const typ = (msg?.type || "").toString().toLowerCase();
  if (typ === "image" || (msg?.image && String(msg.image).trim())) return "[Imagem]";
  if (typ === "video" || (msg?.video && String(msg.video).trim())) return "[Vídeo]";
  if (typ === "audio" || (msg?.audio && String(msg.audio).trim())) return "[Áudio]";
  if (typ === "document" || msg?.document) return "[Documento]";
  if (typ === "location" || msg?.location) return "[Localização]";
  if (typ === "file") return "[Arquivo]";
  if (typ === "interactive") return "[Mensagem interativa]";
  return "[Mensagem]";
}

/**
 * Índice O(1) para achar mensagem citada na thread (id_msg_whatsapp / Message-ID).
 * @param {object[]} messages
 */
export function buildQuotedLookupIndex(messages) {
  const byWa = new Map();
  const byEmail = new Map();
  const byLocalId = new Map();
  if (!Array.isArray(messages)) {
    return {
      find() {
        return null;
      },
    };
  }
  for (const m of messages) {
    const wa = m?.id_msg_whatsapp != null ? String(m.id_msg_whatsapp).trim() : "";
    if (wa) byWa.set(wa, m);
    if (m?.id != null) byLocalId.set(String(m.id).trim(), m);
    const eid = m?.email_id != null ? normalizeEmailMessageId(m.email_id) : "";
    if (eid) byEmail.set(eid, m);
    const tagObj = m?.emailOriginalTags;
    if (tagObj && typeof tagObj === "object") {
      const mid = normalizeEmailMessageId(
        tagObj["Message-ID"] || tagObj["Message-Id"] || tagObj["message-id"]
      );
      if (mid) byEmail.set(mid, m);
    }
  }
  return {
    find(refIds, excludeMessageId) {
      if (!Array.isArray(refIds) || !refIds.length) return null;
      const ex = excludeMessageId != null ? Number(excludeMessageId) : NaN;
      for (const rid of refIds) {
        if (!rid) continue;
        const s = String(rid).trim();
        let hit = byWa.get(s);
        if (hit && Number(hit.id) !== ex) return hit;
        if (/^\d+$/.test(s)) {
          hit = byLocalId.get(s);
          if (hit && Number(hit.id) !== ex) return hit;
        }
        const en = normalizeEmailMessageId(s);
        if (en) {
          hit = byEmail.get(en);
          if (hit && Number(hit.id) !== ex) return hit;
        }
      }
      return null;
    },
  };
}

/**
 * @param {object} message
 * @param {ReturnType<typeof buildQuotedLookupIndex>} quotedLookup
 * @param {object} user
 * @returns {{ author: string, text: string, matchedMessageId: number | string } | null}
 */
export function resolveQuotedPreview(message, quotedLookup, user) {
  if (!message || !quotedLookup || typeof quotedLookup.find !== "function") return null;
  const refIds = extractReplyReferenceIds(message);
  if (refIds.length) {
    const matched = quotedLookup.find(refIds, message.id);
    if (matched) {
      return {
        author: quotedAuthorLabel(matched, user),
        text: quotedSnippetFromMessage(matched),
        matchedMessageId: matched.id,
      };
    }
  }
  const parsed = parseJsonMaybe(message?.json_whatsapp);
  const xt = parsed?.xpro_thread;
  if (xt && typeof xt === "object" && (xt.replyToWaMessageId || xt.quotedSnippet || xt.quotedAuthor)) {
    return {
      author: xt.quotedAuthor || "Mensagem referenciada",
      text: xt.quotedSnippet || "(Prévia indisponível)",
      matchedMessageId: xt.referencedLocalId != null ? xt.referencedLocalId : null,
    };
  }
  const legConv = message?.quotedMessageConversationWhatsapp;
  if (legConv && String(legConv).trim()) {
    const plain = stripHtmlToPlain(String(legConv));
    const t = plain.length > 220 ? `${plain.slice(0, 217)}…` : plain;
    if (t) {
      return {
        author: "Mensagem referenciada",
        text: t,
        matchedMessageId: null,
      };
    }
  }
  return null;
}

/**
 * Rótulo estilo WhatsApp para encaminhamento (Meta / snapshot xpro_thread / API Fácil).
 */
export function getWhatsappForwardBannerLabel(message) {
  const ifw = message?.isForwardedWhatsapp;
  if (ifw === 1 || ifw === true || String(ifw) === "1") return "Encaminhada";
  const parsed = parseJsonMaybe(message?.json_whatsapp);
  if (!parsed) return null;
  const xt = parsed.xpro_thread;
  if (xt && typeof xt === "object" && xt.forwarded) {
    return xt.forwardedMany ? "Encaminhada várias vezes" : "Encaminhada";
  }
  const waMsg = metaWhatsappMessageFromWebhook(parsed);
  if (waMsg && typeof waMsg === "object") {
    const ctx = waMsg.context && typeof waMsg.context === "object" ? waMsg.context : {};
    const many =
      waMsg.forwarded_many_times === true ||
      waMsg.frequently_forwarded === true ||
      ctx.frequently_forwarded === true;
    const fwd =
      waMsg.forwarded === true ||
      ctx.forwarded === true ||
      many;
    if (fwd) return many ? "Encaminhada várias vezes" : "Encaminhada";
  }
  if (
    parsed &&
    typeof parsed === "object" &&
    !parsed.entry &&
    parsed.type !== "message" &&
    !parsed.messaging
  ) {
    if (parsed.encaminhada === true || parsed.forwarded === true || parsed.is_encaminhamento === true) {
      return "Encaminhada";
    }
  }
  return null;
}

/**
 * @param {object} message - objeto de mensagem da API de detalhes do chat
 * @returns {{ chips: Array<{ key: string, label: string, tooltip: string }> }}
 */
export function getMessageThreadHints(message) {
  const chips = [];
  const legQ = pickTruthyString(message?.quotedMessageIdWhatsapp);
  const legFwd = message?.isForwardedWhatsapp;
  if (legQ) {
    chips.push({
      key: "leg-reply",
      label: "Resposta",
      tooltip: "Resposta a outra mensagem (campo legado QuotedMessageIDWhatsapp no banco).",
    });
  }
  if (legFwd === 1 || legFwd === true || String(legFwd) === "1") {
    chips.push({
      key: "leg-fwd",
      label: "Encaminhada",
      tooltip: "Encaminhamento indicado no banco (IsForwardedWhatsapp).",
    });
  }

  const parsed = parseJsonMaybe(message?.json_whatsapp);

  const xtRoot = parsed?.xpro_thread;
  if (xtRoot && typeof xtRoot === "object") {
    if (pickTruthyString(xtRoot.replyToWaMessageId)) {
      chips.push({
        key: "xpro-reply",
        label: "Resposta",
        tooltip: "Resposta a outra mensagem no WhatsApp (dados normalizados no servidor).",
      });
    }
    if (xtRoot.referredProduct?.catalogId || xtRoot.referredProduct?.productRetailerId) {
      chips.push({
        key: "xpro-product",
        label: "Produto do catálogo",
        tooltip: `Consulta sobre produto (catálogo: ${xtRoot.referredProduct.catalogId || "—"}, SKU: ${xtRoot.referredProduct.productRetailerId || "—"}).`,
      });
    }
    if (xtRoot.forwarded) {
      chips.push({
        key: "xpro-fwd",
        label: xtRoot.forwardedMany ? "Encaminhada (várias vezes)" : "Encaminhada",
        tooltip: xtRoot.forwardedMany
          ? "Encaminhamento múltiplo indicado pelo WhatsApp ou pelo provedor."
          : "Mensagem encaminhada.",
      });
    }
  }

  // --- WhatsApp Cloud (Meta): webhook completo em json_whatsapp ---
  const waMsg = metaWhatsappMessageFromWebhook(parsed);
  if (waMsg && typeof waMsg === "object") {
    const ctx = waMsg.context && typeof waMsg.context === "object" ? waMsg.context : {};
    const replyId = pickTruthyString(ctx.id);
    const hasReply = !!replyId;
    let forwarded =
      ctx.forwarded === true ||
      ctx.frequently_forwarded === true ||
      waMsg.forwarded === true ||
      waMsg.frequently_forwarded === true ||
      waMsg.forwarded_many_times === true;
    const forwardedMany =
      ctx.frequently_forwarded === true ||
      waMsg.frequently_forwarded === true ||
      waMsg.forwarded_many_times === true;

    if (hasReply && !chips.some((c) => c.key === "xpro-reply" || c.key === "leg-reply")) {
      chips.push({
        key: "wa-reply",
        label: "Resposta",
        tooltip: `Resposta a uma mensagem no WhatsApp (id: ${replyId.slice(0, 32)}${replyId.length > 32 ? "…" : ""}).`,
      });
    }
    if (forwarded && !chips.some((c) => c.key === "xpro-fwd" || c.key === "wa-fwd" || c.key === "leg-fwd")) {
      chips.push({
        key: "wa-fwd",
        label: forwardedMany ? "Encaminhada (várias vezes)" : "Encaminhada",
        tooltip: forwardedMany
          ? "WhatsApp marcou esta mensagem como encaminhada repetidas vezes."
          : "Mensagem encaminhada no WhatsApp.",
      });
    }
  }

  // --- Gupshup (type: message, payload.payload.context) ---
  if (!chips.length) {
    const gsPayload = gupshupInnerPayload(parsed);
    const inner = gsPayload?.payload && typeof gsPayload.payload === "object" ? gsPayload.payload : null;
    const ctx = inner?.context && typeof inner.context === "object" ? inner.context : null;
    if (ctx) {
      const ref = pickTruthyString(ctx.gsId || ctx.id);
      if (ref) {
        chips.push({
          key: "gs-reply",
          label: "Resposta / citação",
          tooltip: `Mensagem vinculada a outra na Gupshup (contexto: ${ref.slice(0, 40)}${ref.length > 40 ? "…" : ""}).`,
        });
      }
    }
  }

  // --- Messenger / Instagram Direct: entry[0].messaging[0].message ---
  const m = metaMessagingFromWebhook(parsed);
  const fbMsg = m?.message && typeof m.message === "object" ? m.message : null;
  if (fbMsg) {
    const mid = pickTruthyString(fbMsg.reply_to?.mid);
    if (mid) {
      const midShort = mid.length > 28 ? `${mid.slice(0, 28)}…` : mid;
      chips.push({
        key: "meta-reply",
        label: "Resposta",
        tooltip: `Resposta a uma mensagem na conversa (mid: ${midShort}).`,
      });
    }
    if (fbMsg.is_forwarded === true) {
      chips.push({
        key: "meta-fwd",
        label: "Encaminhada",
        tooltip: "Mensagem encaminhada (Meta).",
      });
    }
  }

  // --- API Fácil / outros: chaves comuns quando o webhook as envia ---
  if (
    parsed &&
    typeof parsed === "object" &&
    !parsed.entry &&
    parsed.type !== "message" &&
    !parsed.messaging
  ) {
    const replyRef = pickTruthyString(
      parsed.quoted_message_id ||
        parsed.id_mensagem_respondida ||
        parsed.mensagem_citada_id ||
        parsed.citacao_id ||
        (parsed.mensagem_citada && typeof parsed.mensagem_citada === "object"
          ? parsed.mensagem_citada.id || parsed.mensagem_citada.mensagem_id
          : "")
    );
    const fwd =
      parsed.encaminhada === true ||
      parsed.forwarded === true ||
      parsed.is_encaminhamento === true;

    if (replyRef) {
      chips.push({
        key: "generic-reply",
        label: "Resposta / citação",
        tooltip: `Referência a outra mensagem no provedor (${replyRef.slice(0, 40)}${replyRef.length > 40 ? "…" : ""}).`,
      });
    }
    if (fwd) {
      chips.push({
        key: "generic-fwd",
        label: "Encaminhada",
        tooltip: "Encaminhamento indicado pelo provedor.",
      });
    }
  }

  // --- E-mail: cabeçalhos em emailOriginalTags ---
  const tags = message?.emailOriginalTags;
  if (tags && typeof tags === "object") {
    const irt = pickTruthyString(tags["In-Reply-To"] || tags["in-reply-to"]);
    if (irt) {
      chips.push({
        key: "email-reply",
        label: "Resposta",
        tooltip: "E-mail em resposta a outra mensagem (cabeçalho In-Reply-To).",
      });
    }
  }

  // Dedup por key (Meta WA pode combinar com outros se no futuro houver sobreposição)
  const seen = new Set();
  const unique = [];
  for (const c of chips) {
    if (seen.has(c.key)) continue;
    seen.add(c.key);
    unique.push(c);
  }

  return { chips: unique };
}
